import React, { useMemo } from 'react';

import { formatDate } from '@betterment/core';
import { useCursor } from '@betterment/dataviz';
import { Box } from '@betterment/samba-react';

import { TimeWeightedReturnDisplay } from './TimeWeightedReturnDisplay';

import { PerformanceHistoryTimeWeightedReturnItem } from '../../../../graphql/schema';

export type TimeWeightedReturnsChartDataProps = {
  timeSeries?: PerformanceHistoryTimeWeightedReturnItem[];
  benchmarkTimeSeries?: PerformanceHistoryTimeWeightedReturnItem[];
  benchmarkName?: string;
  loading?: boolean;
  portfolioDescription?: string;
};

export const TimeWeightedReturnsChartData: React.FC<TimeWeightedReturnsChartDataProps> = ({
  timeSeries,
  benchmarkTimeSeries,
  benchmarkName,
  loading = false,
  portfolioDescription = undefined,
}) => {
  const { data: cursorData } = useCursor<PerformanceHistoryTimeWeightedReturnItem>();

  const portfolioMap = useMemo(() => {
    if (!timeSeries) return null;
    const map = new Map<string, PerformanceHistoryTimeWeightedReturnItem>();
    timeSeries.forEach(item => {
      const dateKey = item.date.toISOString();
      map.set(dateKey, item);
    });
    return map;
  }, [timeSeries]);

  const benchmarkMap = useMemo(() => {
    if (!benchmarkTimeSeries) return null;
    const map = new Map<string, PerformanceHistoryTimeWeightedReturnItem>();
    benchmarkTimeSeries.forEach(item => {
      const dateKey = item.date.toISOString();
      map.set(dateKey, item);
    });
    return map;
  }, [benchmarkTimeSeries]);

  if (loading || !timeSeries || timeSeries.length === 0) {
    return null;
  }

  const validEntries = timeSeries.filter(d => d.cumulativeReturn != null);
  const lastEntry = validEntries[validEntries.length - 1];

  if (!cursorData && !lastEntry) {
    return null;
  }

  const cursorDate = cursorData?.date;

  const portfolioDataPoint = cursorDate ? portfolioMap?.get(cursorDate.toISOString()) : lastEntry;

  const benchmarkDataPoint = cursorDate
    ? benchmarkMap?.get(cursorDate.toISOString())
    : benchmarkTimeSeries?.[benchmarkTimeSeries.length - 1];

  const displayDate = cursorDate ? formatDate(cursorDate) : undefined;
  const returnValue = portfolioDataPoint?.cumulativeReturn;
  const benchmarkReturnValue = benchmarkDataPoint?.cumulativeReturn;

  return (
    <Box css={{ marginBottom: 'space.200', display: 'flex', gap: 'space.400' }}>
      <TimeWeightedReturnDisplay
        color="dataviz.blue.2"
        label="Your portfolio"
        returnValue={returnValue}
        displayDate={displayDate}
        description={portfolioDescription}
      />

      {benchmarkTimeSeries && benchmarkTimeSeries.length > 0 && (
        <TimeWeightedReturnDisplay
          color="dataviz.purple.3"
          label={benchmarkName || 'Benchmark'}
          returnValue={benchmarkReturnValue}
          displayDate={displayDate}
        />
      )}
    </Box>
  );
};
